/**
 * The one WebSocket (D8, plan §2.1).
 *
 * There is exactly one socket object in this tab and this file owns it. Nothing
 * else may construct a `WebSocket` — eslint enforces that for components and
 * routes, and the reason is not tidiness: two sockets means two subscription
 * sets, two `seq` spaces and two books that disagree, and the second one is
 * always the one nobody is watching when it goes wrong.
 *
 * What this file is responsible for:
 *
 * - **Connecting, and reconnecting with the one backoff formula.** Full jitter,
 *   `random_uniform(0, min(cap, base × 2^attempt))`, with `base`, `cap` and
 *   `jitter` read from `GET /config.ws` and never written down here (plan §2.12).
 *   At 2,880 boundaries a day across thousands of tabs the herd shape is a
 *   capacity parameter, so it has to be retunable without a client release.
 * - **Feeding the clock.** The `ping` carries `Clock.stampPing()` and the `pong`
 *   goes to `Clock.observePong()` — the continuous, *unbiased* offset source
 *   (register §F11e). Every other server frame's `server_time_ms` goes to
 *   `observeServerTime`, which is free but biased late by the one-way latency.
 *   A pong is never handed to both.
 * - **Discarding the pong window on every reconnect.** Samples taken over the
 *   old path describe a route that no longer exists.
 * - **Knowing that there are no application close codes.** The `4000`/`4008`/
 *   `4009` scheme of the earlier draft is banned by docs/03 Appendix C and does
 *   not exist in `asyncapi`. Buffer overflow, sequence loss, session expiry and
 *   shutdown all arrive as `error` **frames** with `terminal: true`, and the
 *   socket survives them. The one close code with meaning is `1013 Try Again
 *   Later`, where `retry_after_ms` is honoured before the schedule resumes.
 *
 * What it is deliberately **not** responsible for: which channels are held
 * (`subscriptions.ts`), whether a frame is in sequence (`sequencer.ts`), whether
 * a payload has been seen before (`dedupe.ts`), and when to ping
 * (`heartbeat.ts`). This file moves frames and manages one socket's life.
 */

import type { Clock } from '@/lib/clock'
import type { AnyServerFrame, ClientFrame, FrameType, WsProblemData } from './protocol'

// ---------------------------------------------------------------------------
// config.ws
// ---------------------------------------------------------------------------

/**
 * The transport parameters, resolved from `GET /config`.
 *
 * Every field is required here even though `openapi` marks them all optional:
 * the generated type describes what the wire *may* carry, and this describes
 * what the client needs to run. A missing `reconnect_base_ms` has no safe
 * default — inventing one is the hardcoded schedule Appendix C bans — so it
 * fails loudly at boot instead of quietly at the first disconnect.
 */
export interface WsConfig {
  url: string
  ping_interval_ms: number
  hidden_tab_ping_interval_ms: number
  server_idle_close_ms: number
  reconnect_base_ms: number
  reconnect_cap_ms: number
  jitter: 'full'
  max_subscriptions_per_connection: number
  book_depth_default: number
  book_depth_max: number
  checksum_levels: number
}

export class WsConfigError extends Error {
  override readonly name = 'WsConfigError'
  readonly missing: string[]
  constructor(missing: string[]) {
    super(
      `GET /config returned no ${missing.join(', ')} under \`ws\`. ` +
        'Nothing in config.ws is a constant in this client (plan §2.12) — there is no default to fall back to.',
    )
    this.missing = missing
  }
}

const REQUIRED_WS_KEYS = [
  'url',
  'ping_interval_ms',
  'hidden_tab_ping_interval_ms',
  'server_idle_close_ms',
  'reconnect_base_ms',
  'reconnect_cap_ms',
  'jitter',
  'max_subscriptions_per_connection',
  'book_depth_default',
  'book_depth_max',
  'checksum_levels',
] as const satisfies readonly (keyof WsConfig)[]

/** Validate `config.ws` into the shape the transport runs on. */
export function resolveWsConfig(ws: Partial<WsConfig> | undefined | null): WsConfig {
  const source = ws ?? {}
  const missing = REQUIRED_WS_KEYS.filter((key) => source[key] === undefined || source[key] === null)
  if (missing.length > 0) throw new WsConfigError([...missing])
  return { ...(source as WsConfig) }
}

// ---------------------------------------------------------------------------
// The socket, abstracted just enough to test
// ---------------------------------------------------------------------------

/** The slice of `WebSocket` this client uses. The mock implements exactly this. */
export interface SocketLike {
  readonly readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((event: unknown) => void) | null
  onclose: ((event: { code: number; reason: string; wasClean: boolean }) => void) | null
  onerror: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
}

export type SocketFactory = (url: string) => SocketLike

export type ConnectionStatus = 'IDLE' | 'CONNECTING' | 'OPEN' | 'BACKOFF' | 'CLOSED'

export interface CloseInfo {
  code: number
  reason: string
  /** `false` only when the application called `close()`. */
  willReconnect: boolean
  /** Backoff delay actually scheduled, including any honoured `retry_after_ms`. */
  delayMs: number
}

export interface ConnectionHandlers {
  onOpen?: () => void
  /** Every well-formed server frame, in arrival order, on the socket's own turn. */
  onFrame?: (frame: AnyServerFrame) => void
  onClose?: (info: CloseInfo) => void
  onStatus?: (status: ConnectionStatus) => void
  /** A frame that was not JSON, or carried no `type`. Reported, never thrown. */
  onMalformedFrame?: (raw: unknown, error: unknown) => void
}

export interface ConnectionOptions extends ConnectionHandlers {
  config: WsConfig
  clock: Clock
  socketFactory?: SocketFactory
  /** Injected in tests. Full jitter needs a uniform on `[0, 1)`. */
  random?: () => number
  monoNow?: () => number
  setTimer?: (fn: () => void, ms: number) => number
  clearTimer?: (handle: number) => void
}

/**
 * A connection is "stable" once it has been open this long, at which point the
 * backoff attempt counter resets to 0 (asyncapi §5.7).
 *
 * It is a property of the reconnect algorithm rather than a tuning knob, so it
 * is not in `config.ws` and not read from it.
 */
export const STABLE_CONNECTION_MS = 30_000

/**
 * The attempt a **fleet-wide** drop starts its backoff from.
 *
 * §5.7's stable-reset is right about one tab and wrong about all of them at
 * once, and the client cannot tell the two apart from a bare `1006`: an
 * hour-long session that drops once must not inherit the backoff of an outage
 * it survived, and yet after a gateway restart *every* tab has been stable, so
 * every one of them draws its first delay from `U(0, reconnect_base_ms)` — 250
 * ms — each followed by §5.8's mandatory re-snapshot (`docs/17`).
 *
 * There are exactly two drops the server **names** as fleet-wide, and this is
 * what the client does with them rather than throwing them away: `1013 Try
 * Again Later`, and a terminal `SERVER_SHUTDOWN`, which `asyncapi` documents as
 * *"rolling deploy or operator restart. Every subscription is gone"*. For those
 * the reset is skipped and the schedule starts here, so the window is
 * `min(cap, base × 2⁴)` — four seconds at the published constants, against 250
 * ms. **The formula is untouched**; this only chooses which `attempt` a named
 * fleet event resumes at, which is the one number §5.7 leaves to the client.
 *
 * It is *not* in `config.ws`, for `STABLE_CONNECTION_MS`'s reason: `base` and
 * `cap` are the tuning knobs and they are both read from config, so an operator
 * who wants this window wider already has two levers on it. Adding a third that
 * only applies on two close codes would be a herd parameter nobody would find.
 *
 * **What this does not cover, and it is the larger half:** a mass drop that
 * arrives as a bare `1006` — a killed process, a load balancer, a dropped edge
 * node — is indistinguishable from one tab's own Wi-Fi, so it still collapses
 * to one base window. §5.7's own answer to that is `reconnect_base_ms`, which
 * is in config precisely so the herd shape can be retuned without a client
 * release.
 */
export const FLEET_EVENT_ATTEMPT = 4

/** The one close code with client-visible meaning. Everything else is a drop. */
export const CLOSE_TRY_AGAIN_LATER = 1013

export class WsConnection {
  readonly #config: WsConfig
  readonly #clock: Clock
  readonly #socketFactory: SocketFactory
  readonly #random: () => number
  readonly #monoNow: () => number
  readonly #setTimer: (fn: () => void, ms: number) => number
  readonly #clearTimer: (handle: number) => void
  readonly #handlers: ConnectionHandlers

  #socket: SocketLike | null = null
  #status: ConnectionStatus = 'IDLE'
  #attempt = 0
  #openedAtMs: number | null = null
  #reconnectHandle: number | null = null
  /** Set by `close()`, so a deliberate teardown is not mistaken for a drop. */
  #shutdown = false
  /** Honoured before the next backoff delay, then cleared (asyncapi §5.7). */
  #retryAfterMs = 0
  /** The server named this drop fleet-wide. Consumed by the next `#onClose`. */
  #fleetEvent = false
  #frameCounter = 0
  #malformedFrames = 0

  constructor(options: ConnectionOptions) {
    this.#config = options.config
    this.#clock = options.clock
    this.#socketFactory =
      options.socketFactory ?? ((url) => new WebSocket(url) as unknown as SocketLike)
    this.#random = options.random ?? Math.random
    this.#monoNow = options.monoNow ?? (() => performance.now())
    this.#setTimer = options.setTimer ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
    this.#clearTimer = options.clearTimer ?? ((h) => globalThis.clearTimeout(h))
    this.#handlers = options
  }

  get status(): ConnectionStatus {
    return this.#status
  }

  get isOpen(): boolean {
    return this.#status === 'OPEN'
  }

  /** Consecutive failed attempts. Resets after `STABLE_CONNECTION_MS` open. */
  get attempt(): number {
    return this.#attempt
  }

  get malformedFrames(): number {
    return this.#malformedFrames
  }

  get config(): WsConfig {
    return this.#config
  }

  // -------------------------------------------------------------------------

  connect(): void {
    if (this.#status === 'CONNECTING' || this.#status === 'OPEN') return
    this.#shutdown = false
    this.#cancelReconnect()
    this.#setStatus('CONNECTING')

    let socket: SocketLike
    try {
      socket = this.#socketFactory(this.#config.url)
    } catch (error) {
      // A factory that throws is a dead network, not a protocol failure. Treat
      // it exactly like a close so the backoff schedule is the only path.
      this.#handlers.onMalformedFrame?.(null, error)
      this.#onClose({ code: 1006, reason: 'socket construction failed', wasClean: false })
      return
    }

    this.#socket = socket
    // **Every handler answers only while its socket is the current one.** A
    // browser fires `onclose` after the closing handshake, not inside `close()`,
    // so a socket this connection has already let go of can still report — and
    // unguarded, its late `onclose` nulled the socket that replaced it, which
    // was then never closed and kept streaming every channel it held into the
    // same sequencer as its own replacement: two `seq` streams interleaved, a gap
    // on every other frame, a recovery loop that only a reload ended. Each
    // orphan's idle close orphaned the next, so it never ended by itself.
    socket.onopen = () => {
      if (this.#socket === socket) this.#onOpen()
    }
    socket.onclose = (event) => {
      if (this.#socket === socket) this.#onClose(event)
    }
    socket.onerror = () => {
      // `onerror` is followed by `onclose` in every browser. Reconnecting here
      // as well would double-schedule the backoff.
    }
    socket.onmessage = (event) => {
      if (this.#socket === socket) this.#onMessage(event.data)
    }
  }

  /**
   * Deliberate teardown. No reconnect follows — this is the tab going away, not
   * the network dropping.
   *
   * The close is reported here rather than by the socket's own `onclose`, which
   * is detached first: once this connection has let go of a socket, nothing
   * that socket does afterwards may reach it.
   */
  close(code = 1000, reason = 'client shutdown'): void {
    this.#shutdown = true
    this.#cancelReconnect()
    const socket = this.#release()
    this.#setStatus('CLOSED')
    if (!socket) return
    socket.close(code, reason)
    this.#handlers.onClose?.({ code, reason, willReconnect: false, delayMs: 0 })
  }

  /**
   * Give up on a socket that is still nominally open — the heartbeat's verdict
   * that it is dead — and reconnect through the ordinary close path, so the
   * backoff and `onClose` are the same as for a socket the network dropped.
   *
   * `close()` followed by `connect()` was the defect this exists to replace:
   * the old socket's `onclose` arrived after the new one had opened.
   */
  restart(code = 1000, reason = 'restart'): void {
    if (this.#shutdown || !this.#socket) return
    const socket = this.#release()
    socket?.close(code, reason)
    this.#onClose({ code, reason, wasClean: false })
  }

  /** Detach the current socket so that nothing it does later reaches this
   *  connection, and hand it back to be closed. */
  #release(): SocketLike | null {
    const socket = this.#socket
    this.#socket = null
    if (socket) {
      socket.onopen = null
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null
    }
    return socket
  }

  /**
   * Send a client frame.
   *
   * Assigns the correlation `id` the server echoes on the matching `ack`,
   * `error` or `pong`, and stamps `client_time_ms` — for a `ping` that stamp is
   * `Clock.stampPing()`, which is what makes the round trip an NTP sample rather
   * than a liveness poke. Returns the `id` so a caller can correlate; returns
   * `null` if the socket is not open, because a frame queued against a dead
   * socket is a frame that arrives after the resubscribe that replaced it.
   */
  send<D>(type: FrameType, data?: D, options: { id?: string } = {}): string | null {
    const socket = this.#socket
    if (!socket || this.#status !== 'OPEN') return null

    const id = options.id ?? this.#nextId()
    const frame: ClientFrame<D> = {
      type,
      id,
      // `stampPing()` is the module's only wall-clock read and it lives in
      // lib/clock (D9). Every client frame carries it: `t0` for a ping, an
      // ordinary send stamp for everything else.
      client_time_ms: this.#clock.stampPing(),
      ...(data === undefined ? {} : { data }),
    }
    socket.send(JSON.stringify(frame))
    return id
  }

  #nextId(): string {
    this.#frameCounter += 1
    return `c-${this.#frameCounter}`
  }

  // -------------------------------------------------------------------------

  #onOpen(): void {
    this.#openedAtMs = this.#monoNow()
    this.#setStatus('OPEN')
    // A new socket is a new path. The pong window is a median over samples that
    // measured the old one, and a median across two paths is a number neither
    // of them would produce.
    this.#clock.resetPongSamples()
    this.#handlers.onOpen?.()
  }

  #onClose(event: { code: number; reason: string; wasClean: boolean }): void {
    this.#socket = null
    const openMs = this.#openedAtMs === null ? 0 : this.#monoNow() - this.#openedAtMs
    this.#openedAtMs = null

    if (this.#shutdown) {
      this.#setStatus('CLOSED')
      this.#handlers.onClose?.({ code: event.code, reason: event.reason, willReconnect: false, delayMs: 0 })
      return
    }

    if (event.code === CLOSE_TRY_AGAIN_LATER) {
      // `1013` is the server saying *come back later*, which is a statement
      // about the venue and not about this connection — so it is a fleet event
      // whether or not it carries a hint. With one, the hint is the floor and
      // the jitter runs on top of it; and the hint alone does not fix the shape,
      // it moves it, because every tab then waits the same hint and draws from
      // the same window afterwards.
      this.noteFleetEvent()
      this.#retryAfterMs = Math.max(this.#retryAfterMs, parseRetryAfter(event.reason))
    }

    // A connection that lived long enough to be called stable starts the next
    // schedule from zero, so an hour-long session that drops once does not
    // inherit the backoff of an outage it survived (asyncapi §5.7) — **unless
    // the server has said the drop was fleet-wide**, in which case every tab has
    // been stable and the reset is what puts all of them in one 250 ms window.
    // See `FLEET_EVENT_ATTEMPT`.
    if (this.#fleetEvent) this.#attempt = Math.max(this.#attempt, FLEET_EVENT_ATTEMPT)
    else if (openMs >= STABLE_CONNECTION_MS) this.#attempt = 0
    this.#fleetEvent = false

    const delayMs = this.#scheduleReconnect()
    this.#handlers.onClose?.({ code: event.code, reason: event.reason, willReconnect: true, delayMs })
  }

  /**
   * The backoff formula. It exists once, here.
   *
   * `delay = retry_after + random_uniform(0, min(cap, base × 2^attempt))`.
   * Full jitter, not "exponential with ±20%": 500 tabs rolling off one dropped
   * edge node must not come back in lockstep, and the only shape that reliably
   * flattens that is a uniform draw over the whole window.
   */
  #scheduleReconnect(): number {
    const { reconnect_base_ms: base, reconnect_cap_ms: cap } = this.#config
    const window = Math.min(cap, base * 2 ** this.#attempt)
    const jittered = this.#config.jitter === 'full' ? this.#random() * window : window
    const delayMs = Math.round(this.#retryAfterMs + jittered)
    this.#retryAfterMs = 0
    this.#attempt += 1

    this.#setStatus('BACKOFF')
    this.#reconnectHandle = this.#setTimer(() => {
      this.#reconnectHandle = null
      this.connect()
    }, delayMs)
    return delayMs
  }

  #cancelReconnect(): void {
    if (this.#reconnectHandle !== null) {
      this.#clearTimer(this.#reconnectHandle)
      this.#reconnectHandle = null
    }
  }

  /**
   * A `RATE_LIMITED` error frame carries `retry_after_ms`, and it applies to the
   * *next* reconnect rather than to the current socket — the socket is still up
   * and still serving every other subscription (asyncapi §5.7).
   */
  noteRetryAfter(ms: number): void {
    if (Number.isFinite(ms) && ms > 0) this.#retryAfterMs = Math.max(this.#retryAfterMs, ms)
  }

  /**
   * The server has said this drop is about the venue, not about this connection.
   *
   * Called for `1013` here, and by `WsClient` for a terminal `SERVER_SHUTDOWN`
   * — the two events `asyncapi` describes as taking every subscription from
   * every client. Cleared by the close it applies to, so a shutdown frame that
   * is somehow never followed by a close cannot make the next unrelated drop
   * back off for four seconds. See `FLEET_EVENT_ATTEMPT`.
   */
  noteFleetEvent(): void {
    this.#fleetEvent = true
  }

  // -------------------------------------------------------------------------

  #onMessage(raw: unknown): void {
    let frame: AnyServerFrame
    try {
      frame = parseServerFrame(raw)
    } catch (error) {
      this.#malformedFrames += 1
      this.#handlers.onMalformedFrame?.(raw, error)
      return
    }

    if (frame.type === 'pong' && frame.data) {
      // The unbiased path. Deliberately not also fed to `observeServerTime`:
      // applying the biased hint on top of the measurement would drag the
      // estimate back toward the one-way latency it just subtracted.
      const data = frame.data
      this.#clock.observePong({
        clientTimeMs: data.client_time_ms,
        ...(data.recv_time_ms === undefined ? {} : { serverRecvMs: data.recv_time_ms }),
        serverTimeMs: frame.server_time_ms,
      })
    } else if (Number.isFinite(frame.server_time_ms)) {
      this.#clock.observeServerTime(frame.server_time_ms)
    }

    this.#handlers.onFrame?.(frame)
  }

  #setStatus(status: ConnectionStatus): void {
    if (this.#status === status) return
    this.#status = status
    this.#handlers.onStatus?.(status)
  }
}

// ---------------------------------------------------------------------------
// Frame parsing
// ---------------------------------------------------------------------------

export class FrameParseError extends Error {
  override readonly name = 'FrameParseError'
}

const SERVER_FRAME_TYPES: ReadonlySet<string> = new Set([
  'ack',
  'pong',
  'error',
  'heartbeat',
  'snapshot',
  'delta',
  'update',
  'event',
])

/**
 * Parse and minimally validate one server frame.
 *
 * Two invariants are checked and nothing else: `type` must be a server frame
 * type, and `server_time_ms` must be present — "every server frame carries
 * `type` and `server_time_ms`, no exceptions" (docs/03 §4.1). Payload shapes are
 * not validated here. A transport that rejects frames on payload grounds turns a
 * spec drift into a silent outage on one channel, where the store's own missing
 * field turns it into a visibly empty cell.
 */
export function parseServerFrame(raw: unknown): AnyServerFrame {
  const text = typeof raw === 'string' ? raw : null
  if (text === null) throw new FrameParseError(`expected a text frame, got ${typeof raw}`)

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error) {
    throw new FrameParseError(`frame is not JSON: ${(error as Error).message}`)
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new FrameParseError('frame is not a JSON object')
  }

  const frame = parsed as Record<string, unknown>
  if (typeof frame.type !== 'string' || !SERVER_FRAME_TYPES.has(frame.type)) {
    throw new FrameParseError(`unknown server frame type ${JSON.stringify(frame.type)}`)
  }
  if (typeof frame.server_time_ms !== 'number') {
    throw new FrameParseError(`frame ${frame.type} carries no server_time_ms`)
  }
  return frame as unknown as AnyServerFrame
}

/** `1013` may carry the hint in its close reason. Absent or unparseable is 0,
 *  which just means the ordinary schedule applies. */
function parseRetryAfter(reason: string): number {
  const match = /retry_after_ms=(\d+)/.exec(reason)
  return match ? Number(match[1]) : 0
}

/** `true` when the problem names a subscription that no longer exists, so the
 *  client must re-snapshot before resubscribing rather than retrying blind. */
export function isTerminalProblem(problem: WsProblemData): boolean {
  return problem.terminal === true
}
